/*Bearer ${token}`
 * AiChatWidget
 *
 * Floating AI chat bubble that lives in the bottom-right corner of every
 * authenticated page. It injects the current page context (route, client name,
 * job type) into each request so the Gemini assistant answers in context.
 *
 * State is per-session only — history resets when the panel is closed or the
 * page is refreshed.
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { TC, FS, FW } from "tokens/text";
import { sendAiChatMessage, listClientProfiles } from "services/tenantUserAdminService";

// ─── Client section → tab state map ──────────────────────────────────────────
const CLIENT_SECTION_STATE = {
  overview:            { tab: 0 },
  tax:                 { tab: 1 },
  contact:             { tab: 2 },
  address:             { tab: 2 },           // alias
  "registered-address":{ tab: 3 },           // alias → Companies House
  "companies-house":   { tab: 3 },
  ch:                  { tab: 3 },           // alias
  compliance:          { tab: 4 },
  communications:      { tab: 5 },
  email:               { tab: 5 },           // alias
  services:            { tab: 6 },
  jobs:                { tab: 7 },
  "jobs-tasks":        { tab: 7 },           // alias
  cs01:                { tab: 7, jobTab: 3 },
  "confirmation-statement": { tab: 7, jobTab: 3 }, // alias
  charges:             { tab: 8 },
  fees:                { tab: 8 },           // alias
};




// ─── Page context inference ───────────────────────────────────────────────────

const ROUTE_LABELS = {
  "/client-profiles":        "Client List",
  "/organisations":          "Organisations",
  "/app-settings":           "App Settings",
  "/profile":                "User Profile",
  "/dashboard":              "Dashboard",
  "/healthcheck":            "Health Check",
  "/tasks":                  "Task Board",
};

/*Bearer ${token}`
 * Derive a human-readable page name + any extractable IDs from the pathname.
 * @param {string} pathname - react-router pathname
 * @returns {{ pageName: string, clientId?: string }}
 */
function inferPageContext(pathname) {
  // Exact matches first
  for (const [prefix, label] of Object.entries(ROUTE_LABELS)) {
    if (pathname === prefix || pathname.startsWith(prefix + "?")) {
      return { pageName: label };
    }
  }

  // Client profile detail  e.g. /client-profiles/abc123/...
  const cpMatch = pathname.match(/^\/client-profiles\/([^/]+)(\/(.*))?/);
  if (cpMatch) {
    const clientId = cpMatch[1];
    const sub      = cpMatch[3] || "";
    let pageName   = "Client Profile";
    if (sub.startsWith("jobs"))    pageName = "Jobs Board";
    else if (sub.startsWith("tasks")) pageName = "Task Board";
    return { pageName, clientId };
  }

  return { pageName: "Ledger Vision" };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TypingDots() {
  return (
    <span style={{ display: "inline-flex", gap: 3, alignItems: "center" }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: TC.tertiary,
            animation: `aiDotBounce 1.2s ${i * 0.2}s infinite ease-in-out`,
          }}
        />
      ))}
    </span>
  );
}

function MessageBubble({ msg, onNavigate }) {
  const isUser = msg.role === "user";
  return (
    <div
      style={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        marginBottom: 8,
      }}
    >
      <div
        style={{
          maxWidth: "82%",
          padding: "8px 12px",
          borderRadius: isUser ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
          background: isUser
            ? "linear-gradient(135deg, #0057d8 0%, #0075ff 100%)"
            : "#f4f8ff",
          color: isUser ? TC.white : TC.primary,
          fontSize: FS.sm,
          fontWeight: FW.normal,
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          boxShadow: isUser ? "0 2px 8px rgba(0,87,216,0.25)" : "0 1px 4px rgba(10,22,40,0.08)",
        }}
      >
        {msg.content}
        {msg.action && onNavigate && (
          <div style={{ marginTop: 8 }}>
            <button
              onClick={() => onNavigate(msg.action)}
              style={{
                background: "linear-gradient(135deg, #0057d8 0%, #0075ff 100%)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "5px 12px",
                fontSize: FS.xs,
                fontWeight: FW.semibold,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              Go there →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const STORAGE_KEY = "ai_chat_history";

function loadHistory() {
  try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]"); }
  catch { return []; }
}

function saveHistory(msgs) {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(msgs)); }
  catch { /* quota exceeded — ignore */ }
}

// ─── Main widget ──────────────────────────────────────────────────────────────

/*Bearer ${token}`
 * @param {{ tenantId: string, contextData?: { clientName?: string, clientNumber?: string, jobType?: string, extra?: string } }} props
 */
export default function AiChatWidget({ tenantId, contextData }) {
  const { pathname } = useLocation();
  const history                 = useHistory();
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState(loadHistory);
  const [input, setInput]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const messagesEndRef           = useRef(null);
  const inputRef                 = useRef(null);

  // Persist messages to sessionStorage whenever they change
  useEffect(() => { saveHistory(messages); }, [messages]);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Focus input when panel opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setInput("");
    setError(null);
  }, []);

  const handleClearHistory = useCallback(() => {
    setMessages([]);
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: "user", content: text };
    const next    = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setError(null);
    setLoading(true);

    const { pageName, clientId } = inferPageContext(pathname);
    const pageContext = {
      pageName,
      clientId,
      ...(contextData || {}),
    };

    try {
      const { reply, action } = await sendAiChatMessage(tenantId, {
        messages: next,
        pageContext,
      });
      setMessages((prev) => [...prev, { role: "model", content: reply, action: action || null }]);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, pathname, tenantId, contextData]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  if (!tenantId) return null;

  return (
    <>
      {/* Keyframe animation injected once */}
      <style>{`
        @keyframes aiDotBounce {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
          40% { transform: scale(1); opacity: 1; }
        }
        @keyframes aiPanelIn {
          from { opacity: 0; transform: translateY(12px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      {/* Chat panel */}
      {open && (
        <div
          role="dialog"
          aria-labelledby="ai-chat-title"
          aria-describedby="ai-chat-description"
          style={{
            position: "fixed",
            bottom: "6.5rem",
            right: "2rem",
            width: 360,
            height: 480,
            background: "#ffffff",
            borderRadius: 16,
            boxShadow: "0 8px 40px rgba(10,22,40,0.18), 0 2px 12px rgba(10,22,40,0.10)",
            display: "flex",
            flexDirection: "column",
            zIndex: 1400,
            overflow: "hidden",
            animation: "aiPanelIn 0.18s ease-out",
            border: "1px solid #d8e6f7",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "linear-gradient(135deg, #0a1628 0%, #1a3a6e 100%)",
              padding: "14px 16px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <span className="material-icons" style={{ color: "#fff", fontSize: 18 }}>
                smart_toy
              </span>
            </div>
            <div style={{ flex: 1 }}>
              <div id="ai-chat-title" style={{ color: TC.white, fontSize: FS.md, fontWeight: FW.semibold, lineHeight: 1.2 }}>
                AI Assistant
              </div>
              <div id="ai-chat-description" style={{ color: "rgba(255,255,255,0.6)", fontSize: FS.xs }}>
                Ledger Vision · Powered by Gemini
              </div>
            </div>
            <button
              onClick={handleClearHistory}
              aria-label="Clear chat history"
              title="Clear history"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "rgba(255,255,255,0.7)",
                padding: 4,
                display: "flex",
                alignItems: "center",
                borderRadius: 6,
              }}
            >
              <span aria-hidden="true" className="material-icons" style={{ fontSize: 18 }}>delete_sweep</span>
            </button>
            <button
              onClick={handleClose}
              aria-label="Close chat"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "rgba(255,255,255,0.7)",
                padding: 4,
                display: "flex",
                alignItems: "center",
                borderRadius: 6,
              }}
            >
              <span aria-hidden="true" className="material-icons" style={{ fontSize: 20 }}>close</span>
            </button>
          </div>

          {/* Messages */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "12px 14px",
              background: "#fafcff",
            }}
          >
            {messages.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  color: TC.tertiary,
                  fontSize: FS.sm,
                  marginTop: 40,
                  padding: "0 16px",
                  lineHeight: 1.6,
                }}
              >
                <span
                  className="material-icons"
                  style={{ fontSize: 36, color: "#d8e6f7", display: "block", marginBottom: 8 }}
                >
                  smart_toy
                </span>
                Ask me anything about your clients, filings, tasks, or how to use this platform.
              </div>
            )}
            {messages.map((msg, i) => (
              <MessageBubble
                key={i}
                msg={msg}
                onNavigate={async (action) => {
                  if (action.type === "navigate") {
                    history.push(action.route);
                    handleClose();
                  } else if (action.type === "navigate_client") {
                    const section = action.section || "overview";
                    const state = CLIENT_SECTION_STATE[section] || { tab: 0 };
                    try {
                      const { clientProfiles } = await listClientProfiles({ tenantId, status: "active" });
                      const needle = action.clientName.toLowerCase().trim();
                      const match = (clientProfiles || []).find(
                        (p) => (p.legalName || p.tradingName || "").toLowerCase().includes(needle)
                      );
                      if (match) {
                        history.push(`/client-profiles/${match.id}`, state);
                        handleClose();
                      } else {
                        setMessages((prev) => [...prev, {
                          role: "model",
                          content: `I couldn't find a client matching "${action.clientName}". Please check the name and try again, or search in Client Profiles.`,
                          action: { type: "navigate", route: "/client-profiles" },
                        }]);
                      }
                    } catch {
                      setMessages((prev) => [...prev, {
                        role: "model",
                        content: "Sorry, I couldn't look up client profiles right now. Please try navigating manually.",
                      }]);
                    }
                  }
                }}
              />
            ))}
            {loading && (
              <div role="status" aria-live="polite" style={{ display: "flex", justifyContent: "flex-start", marginBottom: 8 }}>
                <div
                  style={{
                    padding: "10px 14px",
                    background: "#f4f8ff",
                    borderRadius: "14px 14px 14px 4px",
                    boxShadow: "0 1px 4px rgba(10,22,40,0.08)",
                  }}
                >
                  <TypingDots />
                </div>
              </div>
            )}
            {error && (
              <div
                style={{
                  background: "#fff5f5",
                  border: "1px solid #ffcdd2",
                  borderRadius: 8,
                  padding: "8px 12px",
                  color: TC.error,
                  fontSize: FS.xs,
                  marginTop: 4,
                }}
              >
                {error}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input bar */}
          <div
            style={{
              borderTop: "1px solid #e8f0fb",
              padding: "10px 12px",
              display: "flex",
              gap: 8,
              alignItems: "flex-end",
              background: "#ffffff",
              flexShrink: 0,
            }}
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              aria-label="Ask a question"
              placeholder="Ask a question…"
              rows={1}
              style={{
                flex: 1,
                border: "1.5px solid #d8e6f7",
                borderRadius: 10,
                padding: "8px 12px",
                fontSize: FS.sm,
                color: TC.primary,
                background: "#f4f8ff",
                resize: "none",
                outline: "none",
                fontFamily: "inherit",
                lineHeight: 1.5,
                maxHeight: 96,
                overflowY: "auto",
                transition: "border-color 0.15s",
              }}
              onFocus={(e) => { e.target.style.borderColor = "#0057d8"; }}
              onBlur={(e)  => { e.target.style.borderColor = "#d8e6f7"; }}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              aria-label="Send message"
              style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                border: "none",
                background: !input.trim() || loading
                  ? "#e8f0fb"
                  : "linear-gradient(135deg, #0057d8 0%, #0075ff 100%)",
                cursor: !input.trim() || loading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                transition: "background 0.15s",
                boxShadow: !input.trim() || loading ? "none" : "0 2px 8px rgba(0,87,216,0.3)",
              }}
            >
              <span
                className="material-icons"
                style={{
                  fontSize: 18,
                  color: !input.trim() || loading ? TC.tertiary : "#fff",
                }}
              >
                send
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Floating bubble button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close AI assistant" : "Open AI assistant"}
        title="AI Assistant"
        style={{
          position: "fixed",
          bottom: "2rem",
          right: "6rem",
          width: 52,
          height: 52,
          borderRadius: "50%",
          border: "none",
          background: open
            ? "linear-gradient(135deg, #1a3a6e 0%, #0a1628 100%)"
            : "linear-gradient(135deg, #0057d8 0%, #0075ff 100%)",
          boxShadow: "0 4px 20px rgba(0,87,216,0.4)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1300,
          transition: "background 0.2s, box-shadow 0.2s, transform 0.15s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.08)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
      >
        <span
          className="material-icons"
          style={{ color: "#fff", fontSize: 24 }}
        >
          {open ? "close" : "smart_toy"}
        </span>
      </button>
    </>
  );
}


